import React, { useState, useRef, useEffect } from 'react'
import { toast } from 'react-hot-toast'
import { 
  api, 
  getCampaignInstructions, 
  generateContentWithSearch, 
  generateImageWithStyle, 
  generateTitlesHeadlines
} from '../lib/api'
import { useAuth } from '../contexts/AuthContext'

interface AICreativeAssistantProps {
  onContentGenerated?: (content: any) => void
  onImageGenerated?: (assets: any[]) => void
  clientId?: number
}

interface CampaignInstruction {
  name: string
  description: string
  system_prompt: string
  image_style: string
}

interface SearchResult {
  title: string
  snippet: string
  url: string
}

interface GeneratedContent {
  content?: any
  campaign_type?: string
  search_results?: Array<{
    title: string
    snippet: string
    url: string
  }>
  images?: Array<{
    prompt: string
    style: string
    concept: string
  }>
  titles?: Array<{
    text: string
    approach: string
    target_emotion: string
  }>
  headlines?: Array<{
    text: string
    length: string
    style: string
    cta_focus: string
  }>
}

interface DataAnalysis {
  success: boolean
  filename: string
  data_type: string
  extracted_content: string
  full_content: string
  analysis_metadata: {
    file_size: number
    content_length: number
    brand_context: string
    campaign_objective: string
    target_audience: string
  }
}

interface ContentTypeConfig {
  generateImages: boolean
  generateTitles: boolean
  generateHeadlines: boolean
  imageQuantity: number
  titleQuantity: number
  headlineQuantity: number
}

export default function AICreativeAssistant({ 
  onContentGenerated, 
  onImageGenerated, 
  clientId 
}: AICreativeAssistantProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [currentStep, setCurrentStep] = useState<'upload' | 'analyze' | 'generate' | 'results'>('upload')
  const [loading, setLoading] = useState(false)
  const [dataAnalysis, setDataAnalysis] = useState<DataAnalysis | null>(null)
  const [generatedContent, setGeneratedContent] = useState<GeneratedContent | null>(null)
  const [selectedImagePrompts, setSelectedImagePrompts] = useState<string[]>([])
  
  // New campaign style and search functionality
  const [campaignInstructions, setCampaignInstructions] = useState<Record<string, CampaignInstruction>>({})
  const [selectedCampaignType, setSelectedCampaignType] = useState<string>('soft_sale')
  const [useWebSearch, setUseWebSearch] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])
  const [searchLoading, setSearchLoading] = useState(false)
  
  // Form data
  const [inputMethod, setInputMethod] = useState<'file' | 'text' | 'quick' | 'ai_search'>('quick')
  const [manualText, setManualText] = useState('')
  const [productInfo, setProductInfo] = useState('')
  
  // Content type configuration
  const [contentConfig, setContentConfig] = useState<ContentTypeConfig>({
    generateImages: true,
    generateTitles: true,
    generateHeadlines: true,
    imageQuantity: 4,
    titleQuantity: 5,
    headlineQuantity: 5
  })
  
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { user, loading: authLoading } = useAuth()

  // Load campaign instructions on component mount (only when authenticated)
  useEffect(() => {
    if (authLoading) {
      return // Wait for auth to complete
    }
    
    if (user) {
      loadCampaignInstructions()
    }
  }, [user, authLoading])

  const loadCampaignInstructions = async () => {
    try {
      const response = await getCampaignInstructions()
      if (response.data.success) {
        setCampaignInstructions(response.data.campaign_types)
      }
    } catch (error) {
      console.error('Failed to load campaign instructions:', error)
    }
  }

  const handleWebSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter a search query or website to analyze')
      return
    }

    setSearchLoading(true)
    try {
      const response = await api.post('/ai-creative/search', {
        query: searchQuery,
        max_results: 5
      })

      if (response.data.success) {
        setSearchResults(response.data.results)
        setUseWebSearch(true)
        
        // Show different success messages based on search type
        if (response.data.search_type === 'website_analysis') {
          toast.success(`🔍 Website analysis complete! Found ${response.data.results.length} insights`)
        } else {
          toast.success(`🌐 Web search complete! Found ${response.data.results.length} results`)
        }
      }
    } catch (error: any) {
      console.error('Web search failed:', error)
      
      if (error.code === 'ECONNABORTED' || error.name === 'AbortError') {
        toast.error(`🕐 Search timed out after 2 minutes. Try a more specific query or try again.`)
      } else if (error.response?.status === 504) {
        toast.error(`🕐 Server timeout. The search is taking longer than expected. Try again.`)
      } else if (error.response?.data?.search_type) {
        const searchType = error.response.data.search_type
        if (searchType === 'timeout') {
          toast.error(`🕐 Search timed out. Try a more specific query or try again.`)
        } else if (searchType === 'api_error') {
          toast.error(`🔧 API error occurred. Please try again.`)
        } else {
          toast.error(`❌ Search failed: ${error.response.data.message || 'Unknown error'}`)
        }
      } else {
        toast.error('🌐 Web search failed. Please try again.')
      }
    } finally {
      setSearchLoading(false)
    }
  }

  const handleQuickGenerate = async () => {
    if (!productInfo.trim()) {
      toast.error('Please enter product information')
      return
    }

    setLoading(true)
    try {
      // Generate content with search if enabled
      const contentPrompt = `Create marketing content for: ${productInfo}`
      
      const contentResponse = await generateContentWithSearch({
        prompt: contentPrompt,
        campaign_type: selectedCampaignType,
        search_query: useWebSearch ? searchQuery : undefined,
        include_search: useWebSearch
      })

      // Generate titles and headlines separately for better control
      const titlesResponse = await generateTitlesHeadlines({
        product_info: productInfo,
        target_audience: "General audience",
        brand_context: useWebSearch ? `Based on search results for: ${searchQuery}` : "",
        num_titles: contentConfig.titleQuantity,
        num_headlines: contentConfig.headlineQuantity
      })

      // Process the generated content
      const newContent = {
        content: contentResponse.data.content,
        campaign_type: contentResponse.data.campaign_type,
        search_results: contentResponse.data.search_results,
        titles: titlesResponse.data.titles || [],
        headlines: titlesResponse.data.headlines || []
      }
      
      setGeneratedContent(newContent)
      toast.success('Creative content generated!')

      try { onContentGenerated?.(newContent) } catch (e) {
        console.error('onContentGenerated callback error (non-blocking):', e)
      }

      // Generate images separately — don't let image failures kill the content flow
      if (contentConfig.generateImages) {
        generateStyleImages().catch((imgErr) => {
          console.error('Image generation failed (non-blocking):', imgErr)
        })
      }

      setIsOpen(false)
      resetState()

    } catch (error: any) {
      console.error('Content generation failed:', error)
      toast.error(error.response?.data?.detail || 'Content generation failed')
    } finally {
      setLoading(false)
    }
  }

  const generateStyleImages = async () => {
    try {
      const imagePrompt = `Marketing image for: ${productInfo}`
      
      for (let i = 0; i < contentConfig.imageQuantity; i++) {
        const response = await generateImageWithStyle({
          prompt: imagePrompt,
          campaign_type: selectedCampaignType,
          size: '1024x1024',
          client_id: clientId
        })

        if (response.data.success) {
          const imageData = {
            image_url: response.data.image_url,
            prompt: response.data.original_prompt,
            style_enhanced_prompt: response.data.style_enhanced_prompt,
            campaign_type: response.data.campaign_type
          }
          onImageGenerated?.([imageData])
          toast.success('🎨 Image generated successfully!')
        }
      }
    } catch (error: any) {
      console.error('Image generation failed:', error)
      toast.error('Image generation failed')
    }
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    setLoading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('brand_context', "")
      formData.append('campaign_objective', "Brand Awareness")
      formData.append('target_audience', "General audience")

      const response = await api.post('/ai-creative/analyze-data', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      setDataAnalysis(response.data)
      setCurrentStep('analyze')
      toast.success('Data analyzed successfully!')
    } catch (error: any) {
      console.error('Analysis failed:', error)
      toast.error(error.response?.data?.detail || 'Analysis failed')
    } finally {
      setLoading(false)
    }
  }

  const handleManualTextSubmit = async () => {
    if (!manualText.trim()) {
      toast.error('Please enter some text content')
      return
    }

    setLoading(true)
    try {
      // Create a mock analysis for manual text
      const mockAnalysis: DataAnalysis = {
        success: true,
        filename: 'Manual Text Input',
        data_type: 'text',
        extracted_content: manualText.substring(0, 500) + (manualText.length > 500 ? '...' : ''),
        full_content: manualText,
        analysis_metadata: {
          file_size: new Blob([manualText]).size,
          content_length: manualText.length,
          brand_context: "",
          campaign_objective: "Brand Awareness",
          target_audience: "General audience"
        }
      }

      setDataAnalysis(mockAnalysis)
      setCurrentStep('analyze')
      toast.success('Text content analyzed successfully!')
    } catch (error: any) {
      console.error('Analysis failed:', error)
      toast.error('Text analysis failed')
    } finally {
      setLoading(false)
    }
  }

  const generateContent = async () => {
    if (!dataAnalysis) return

    // Validate that at least one content type is selected
    if (!contentConfig.generateImages && !contentConfig.generateTitles && !contentConfig.generateHeadlines) {
      toast.error('Please select at least one content type to generate')
      return
    }

    setLoading(true)
    try {
      // Determine target type based on configuration
      let targetType = 'all'
      if (contentConfig.generateImages && !contentConfig.generateTitles && !contentConfig.generateHeadlines) {
        targetType = 'image'
      } else if (!contentConfig.generateImages && contentConfig.generateTitles && !contentConfig.generateHeadlines) {
        targetType = 'title'
      } else if (!contentConfig.generateImages && !contentConfig.generateTitles && contentConfig.generateHeadlines) {
        targetType = 'headline'
      }

      const response = await api.post('/ai-creative/generate-content', {
        data_content: dataAnalysis.full_content,
        data_type: dataAnalysis.data_type,
        target_type: targetType,
        brand_context: "",
        campaign_objective: "Brand Awareness",
        target_audience: "General audience",
        style_preferences: { 
          style: 'professional', 
          modern: true,
          quantities: {
            images: contentConfig.imageQuantity,
            titles: contentConfig.titleQuantity,
            headlines: contentConfig.headlineQuantity
          },
          generate_flags: {
            images: contentConfig.generateImages,
            titles: contentConfig.generateTitles,
            headlines: contentConfig.generateHeadlines
          }
        }
      })

      if (response.data.success) {
        setGeneratedContent(response.data.generated_content)
        setCurrentStep('results')
        toast.success('Creative content generated!')
        onContentGenerated?.(response.data.generated_content)
      } else {
        toast.error(response.data.error || 'Content generation failed')
      }
    } catch (error: any) {
      console.error('Content generation failed:', error)
      toast.error(error.response?.data?.detail || 'Content generation failed')
    } finally {
      setLoading(false)
    }
  }

  const generateImages = async () => {
    if (!selectedImagePrompts.length || !clientId) {
      toast.error('Please select image prompts and ensure client is selected')
      return
    }

    setLoading(true)
    try {
      const formData = new FormData()
      selectedImagePrompts.forEach(prompt => {
        formData.append('prompts', prompt)
      })
      formData.append('client_id', clientId.toString())
      formData.append('size', '1024x1024')
      formData.append('quality', 'standard')

      const response = await api.post('/ai-creative/generate-multiple-images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })

      if (response.data.success && response.data.generated_assets?.length > 0) {
        toast.success(`Generated ${response.data.generated_assets.length} AI images!`)
        onImageGenerated?.(response.data.generated_assets)
        setIsOpen(false)
        resetState()
      } else {
        toast.error('No images were generated')
      }
    } catch (error: any) {
      console.error('Image generation failed:', error)
      toast.error(error.response?.data?.detail || 'Image generation failed')
    } finally {
      setLoading(false)
    }
  }

  const resetState = () => {
    setCurrentStep('upload')
    setDataAnalysis(null)
    setGeneratedContent(null)
    setSelectedImagePrompts([])
    setInputMethod('quick')
    setManualText('')
    setProductInfo('')
    setSelectedCampaignType('soft_sale')
    setUseWebSearch(false)
    setSearchQuery('')
    setSearchResults([])
    setContentConfig({
      generateImages: true,
      generateTitles: true,
      generateHeadlines: true,
      imageQuantity: 4,
      titleQuantity: 5,
      headlineQuantity: 5
    })
  }

  const toggleImagePrompt = (prompt: string) => {
    setSelectedImagePrompts(prev => 
      prev.includes(prompt) 
        ? prev.filter(p => p !== prompt)
        : [...prev, prompt]
    )
  }

  if (!isOpen) {
    return (
      <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 1000 }}>
        <button
          onClick={() => setIsOpen(true)}
          style={{
            background: 'var(--gradient-primary)',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '60px',
            height: '60px',
            fontSize: '24px',
            cursor: 'pointer',
            boxShadow: 'var(--shadow-button)',
            transition: 'transform 0.2s, box-shadow 0.2s',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)'
            e.currentTarget.style.boxShadow = 'var(--shadow-buttonHover)'
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)'
            e.currentTarget.style.boxShadow = 'var(--shadow-button)'
          }}
        >
          <img 
            src="/marti_logo.png" 
            alt="Marti AI Assistant" 
            style={{
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              objectFit: 'cover',
              background: 'white',
              padding: '4px'
            }}
          />
        </button>
      </div>
    )
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'var(--color-modalOverlay)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    }}>
      <div style={{
        background: 'var(--color-systemBackground)',
        borderRadius: '16px',
        width: '90%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: 'var(--shadow-appleModal)',
      }}>
        {/* Header */}
        <div style={{
          padding: 'var(--spacing-4)',
          borderBottom: '1px solid var(--color-border)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
            <span style={{ fontSize: '24px' }}>🤖</span>
                            <h2 style={{ margin: 0, color: 'var(--color-label)' }}>Marti AI Assistant</h2>
          </div>
          <button
            onClick={() => { setIsOpen(false); resetState() }}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: 'pointer',
              color: 'var(--color-secondaryLabel)',
            }}
          >
            ✕
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: 'var(--spacing-4)' }}>
          {/* Step 1: Upload Data */}
          {currentStep === 'upload' && (
            <div>
              <h3 style={{ color: 'var(--color-label)', marginBottom: 'var(--spacing-3)' }}>
                📊 Upload Your Data
              </h3>
              <p style={{ color: 'var(--color-secondaryLabel)', marginBottom: 'var(--spacing-4)' }}>
                Upload a file with your campaign data (PDF, CSV, JSON, text) to generate AI-powered creative content.
              </p>

              {/* Content Input Section */}
              <div style={{ marginTop: 'var(--spacing-4)' }}>
                <label style={{ 
                  display: 'block', 
                  marginBottom: 'var(--spacing-2)', 
                  fontWeight: 600,
                  color: 'var(--color-label)',
                  fontSize: 'var(--font-size-lg)'
                }}>
                  📝 Content Source
                </label>
                
                {/* Content Input Method Tabs */}
                <div style={{ 
                  display: 'flex', 
                  gap: 'var(--spacing-1)',
                  marginBottom: 'var(--spacing-3)',
                  borderBottom: '1px solid var(--color-border)',
                  paddingBottom: 'var(--spacing-2)'
                }}>
                  {[
                    { key: 'quick', icon: '⚡', label: 'Quick Input' },
                    { key: 'text', icon: '✏️', label: 'Manual Text' },
                    { key: 'file', icon: '📄', label: 'Upload File' },
                    { key: 'ai_search', icon: '🔍', label: 'AI Search' }
                  ].map(method => (
                    <button
                      key={method.key}
                      onClick={() => setInputMethod(method.key as any)}
                      style={{
                        padding: 'var(--spacing-2) var(--spacing-3)',
                        border: 'none',
                        borderRadius: '8px 8px 0 0',
                        background: inputMethod === method.key ? 'var(--color-primary)' : 'var(--color-systemGroupedBackground)',
                        color: inputMethod === method.key ? 'white' : 'var(--color-label)',
                        cursor: 'pointer',
                        fontWeight: inputMethod === method.key ? 600 : 500,
                        fontSize: 'var(--font-size-sm)',
                        transition: 'all 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-1)'
                      }}
                    >
                      <span>{method.icon}</span>
                      {method.label}
                    </button>
                  ))}
                </div>

                {/* Content Input Areas */}
                <div style={{
                  background: 'var(--color-systemGroupedBackground)',
                  borderRadius: '0 12px 12px 12px',
                  padding: 'var(--spacing-4)',
                  border: '1px solid var(--color-border)'
                }}>
                  
                  {/* Quick Input */}
                  {inputMethod === 'quick' && (
                    <div>
                      <h4 style={{ 
                        margin: '0 0 var(--spacing-2) 0',
                        color: 'var(--color-label)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-1)'
                      }}>
                        ⚡ Quick Product Information
                      </h4>
                      <p style={{ 
                        color: 'var(--color-secondaryLabel)', 
                        fontSize: 'var(--font-size-sm)',
                        margin: '0 0 var(--spacing-3) 0'
                      }}>
                        Quickly describe your product or service for instant AI content generation.
                      </p>
                      <textarea
                        value={productInfo}
                        onChange={(e) => setProductInfo(e.target.value)}
                        placeholder="e.g., Eco-friendly bamboo toothbrushes for environmentally conscious families. Biodegradable bristles, sustainable packaging, available in 4 colors..."
                        rows={4}
                        style={{
                          width: '100%',
                          padding: 'var(--spacing-3)',
                          border: '1px solid var(--color-border)',
                          borderRadius: '8px',
                          background: 'var(--color-systemBackground)',
                          color: 'var(--color-label)',
                          resize: 'vertical',
                          fontFamily: 'inherit',
                          fontSize: 'var(--font-size-base)'
                        }}
                      />
                    </div>
                  )}

                  {/* Manual Text Input */}
                  {inputMethod === 'text' && (
                    <div>
                      <h4 style={{ 
                        margin: '0 0 var(--spacing-2) 0',
                        color: 'var(--color-label)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-1)'
                      }}>
                        ✏️ Manual Text Content
                      </h4>
                      <p style={{ 
                        color: 'var(--color-secondaryLabel)', 
                        fontSize: 'var(--font-size-sm)',
                        margin: '0 0 var(--spacing-3) 0'
                      }}>
                        Paste or type your existing content, research, or detailed product information.
                      </p>
                      <textarea
                        value={manualText}
                        onChange={(e) => setManualText(e.target.value)}
                        placeholder="Paste your existing content here...

Examples:
• Product specifications and features
• Market research data
• Brand guidelines
• Customer feedback
• Competitor analysis"
                        rows={8}
                        style={{
                          width: '100%',
                          padding: 'var(--spacing-3)',
                          border: '1px solid var(--color-border)',
                          borderRadius: '8px',
                          background: 'var(--color-systemBackground)',
                          color: 'var(--color-label)',
                          resize: 'vertical',
                          fontFamily: 'inherit',
                          fontSize: 'var(--font-size-base)'
                        }}
                      />
                    </div>
                  )}

                  {/* File Upload */}
                  {inputMethod === 'file' && (
                    <div>
                      <h4 style={{ 
                        margin: '0 0 var(--spacing-2) 0',
                        color: 'var(--color-label)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-1)'
                      }}>
                        📄 Upload Content File
                      </h4>
                      <p style={{ 
                        color: 'var(--color-secondaryLabel)', 
                        fontSize: 'var(--font-size-sm)',
                        margin: '0 0 var(--spacing-3) 0'
                      }}>
                        Upload documents containing your campaign data, product info, or research materials.
                      </p>
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        style={{
                          border: '2px dashed var(--color-border)',
                          borderRadius: '12px',
                          padding: 'var(--spacing-6)',
                          textAlign: 'center',
                          cursor: 'pointer',
                          background: 'var(--color-systemBackground)',
                          transition: 'all 0.2s',
                        }}
                      >
                        <div style={{ fontSize: '48px', marginBottom: 'var(--spacing-2)' }}>📄</div>
                        <p style={{ color: 'var(--color-label)', fontWeight: 600, marginBottom: 'var(--spacing-1)' }}>
                          Click to Upload File
                        </p>
                        <p style={{ color: 'var(--color-secondaryLabel)', fontSize: 'var(--font-size-sm)', margin: 0 }}>
                          Supports PDF, DOCX, CSV, JSON, and text files (Max 10MB)
                        </p>
                      </div>
                    </div>
                  )}

                  {/* AI Website Analysis */}
                  {inputMethod === 'ai_search' && (
                    <div>
                      <h4 style={{ 
                        margin: '0 0 var(--spacing-2) 0',
                        color: 'var(--color-label)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-1)'
                      }}>
                        🔍 AI Website Analysis & Search
                      </h4>
                      <p style={{ 
                        color: 'var(--color-secondaryLabel)', 
                        fontSize: 'var(--font-size-sm)',
                        margin: '0 0 var(--spacing-3) 0'
                      }}>
                        Analyze competitor websites for marketing messages and tactics, or search for industry insights.
                      </p>
                      
                      {/* Search Query Input */}
                      <div style={{ marginBottom: 'var(--spacing-3)' }}>
                        <label style={{ 
                          display: 'block', 
                          marginBottom: 'var(--spacing-1)', 
                          fontWeight: 600,
                          color: 'var(--color-label)',
                          fontSize: 'var(--font-size-sm)'
                        }}>
                          Website Analysis or Search Query
                        </label>
                        <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                          <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="e.g., 'Analyze website.com for hard sale tactics' or 'sustainable skincare marketing trends'"
                            style={{
                              flex: 1,
                              padding: 'var(--spacing-2)',
                              border: '1px solid var(--color-border)',
                              borderRadius: '8px',
                              background: 'var(--color-systemBackground)',
                              color: 'var(--color-label)',
                            }}
                          />
                          <button
                            onClick={handleWebSearch}
                            disabled={searchLoading || !searchQuery.trim()}
                            style={{
                              padding: '0 var(--spacing-3)',
                              background: searchLoading || !searchQuery.trim() ? 'var(--color-systemGray)' : 'var(--color-primary)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              fontWeight: 600,
                              cursor: searchLoading || !searchQuery.trim() ? 'not-allowed' : 'pointer',
                              minWidth: '90px'
                            }}
                          >
                            {searchLoading ? '🔄' : '🎯 Analyze'}
                          </button>
                        </div>
                        
                        {/* Quick Examples */}
                        <div style={{ 
                          marginTop: 'var(--spacing-2)',
                          display: 'flex',
                          gap: 'var(--spacing-1)',
                          flexWrap: 'wrap'
                        }}>
                          {[
                            'Analyze underoutfit.com for hard sale tactics',
                            'Check competitor pricing strategies',
                            'Find urgency marketing examples'
                          ].map((example, i) => (
                            <button
                              key={i}
                              onClick={() => setSearchQuery(example)}
                              style={{
                                padding: 'var(--spacing-1) var(--spacing-2)',
                                background: 'var(--color-systemGray6)',
                                border: '1px solid var(--color-border)',
                                borderRadius: '6px',
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-secondaryLabel)',
                                cursor: 'pointer',
                                transition: 'all 0.2s'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'var(--color-systemGray5)'
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'var(--color-systemGray6)'
                              }}
                            >
                              {example}
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Marketing Analysis Results */}
                      {searchResults.length > 0 && (
                        <div style={{ 
                          marginTop: 'var(--spacing-3)', 
                          padding: 'var(--spacing-3)',
                          background: 'var(--color-systemBackground)',
                          borderRadius: '8px',
                          border: '1px solid var(--color-border)'
                        }}>
                          <h5 style={{ 
                            margin: '0 0 var(--spacing-2) 0', 
                            fontWeight: 600, 
                            color: 'var(--color-label)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--spacing-1)'
                          }}>
                            🎯 Marketing Analysis Results ({searchResults.length} insights found):
                          </h5>
                          {searchResults.map((result, index) => (
                            <div key={index} style={{ 
                              marginBottom: 'var(--spacing-3)',
                              paddingBottom: 'var(--spacing-2)',
                              borderBottom: index < searchResults.length - 1 ? '1px solid var(--color-border)' : 'none'
                            }}>
                              <h6 style={{ 
                                margin: '0 0 var(--spacing-1) 0', 
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 600,
                                color: 'var(--color-label)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-1)'
                              }}>
                                💡 {result.title}
                              </h6>
                              <div style={{ 
                                margin: '0 0 var(--spacing-1) 0', 
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-secondaryLabel)',
                                lineHeight: 1.5,
                                background: 'var(--color-systemGroupedBackground)',
                                padding: 'var(--spacing-2)',
                                borderRadius: '6px',
                                borderLeft: '3px solid var(--color-primary)'
                              }}>
                                {result.snippet}
                              </div>
                              {result.url && (
                                <a 
                                  href={result.url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  style={{ 
                                    fontSize: 'var(--font-size-xs)',
                                    color: 'var(--color-primary)',
                                    textDecoration: 'none',
                                    fontWeight: 500
                                  }}
                                >
                                  🔗 View Source Website →
                                </a>
                              )}
                            </div>
                          ))}
                          
                          {/* Auto-generate content from analysis */}
                          <div style={{ marginTop: 'var(--spacing-3)', textAlign: 'center' }}>
                            <button
                              onClick={() => {
                                // Use analysis results as content base
                                const analysisContent = searchResults.map(r => `${r.title}:\n${r.snippet}`).join('\n\n---\n\n');
                                setManualText(analysisContent);
                                setInputMethod('text');
                                toast.success('Marketing analysis added to content base!');
                              }}
                              style={{
                                padding: 'var(--spacing-2) var(--spacing-4)',
                                background: 'var(--color-success)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '8px',
                                fontWeight: 600,
                                cursor: 'pointer',
                                fontSize: 'var(--font-size-sm)'
                              }}
                            >
                              ✨ Use Marketing Insights as Content Base
                            </button>
                          </div>
                        </div>
                      )}

                      {/* Quick Search Suggestions */}
                      {!searchResults.length && (
                        <div style={{ 
                          background: 'var(--color-systemBackground)',
                          borderRadius: '8px',
                          padding: 'var(--spacing-3)',
                          border: '1px solid var(--color-border)'
                        }}>
                          <p style={{ 
                            margin: 0,
                            color: 'var(--color-secondaryLabel)',
                            fontSize: 'var(--font-size-sm)',
                            textAlign: 'center',
                            fontStyle: 'italic'
                          }}>
                            Enter a search query above to find relevant market insights
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                              </div>

                {/* Campaign Style Selection */}
                <div style={{ marginTop: 'var(--spacing-4)' }}>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: 'var(--spacing-2)', 
                    fontWeight: 600,
                    color: 'var(--color-label)',
                    fontSize: 'var(--font-size-lg)'
                  }}>
                    🎯 Campaign Style
                  </label>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                    gap: 'var(--spacing-2)' 
                  }}>
                    {Object.entries(campaignInstructions).map(([key, instruction]) => (
                      <div
                        key={key}
                        onClick={() => setSelectedCampaignType(key)}
                        style={{
                          padding: 'var(--spacing-3)',
                          border: `2px solid ${selectedCampaignType === key ? 'var(--color-primary)' : 'var(--color-border)'}`,
                          borderRadius: '12px',
                          background: selectedCampaignType === key ? 'var(--color-primaryTranslucent)' : 'var(--color-systemBackground)',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                        }}
                      >
                        <h4 style={{ 
                          margin: '0 0 var(--spacing-1) 0', 
                          color: 'var(--color-label)',
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 600
                        }}>
                          {instruction.name}
                        </h4>
                        <p style={{ 
                          margin: 0, 
                          color: 'var(--color-secondaryLabel)',
                          fontSize: 'var(--font-size-xs)',
                          lineHeight: 1.3
                        }}>
                          {instruction.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Web Search Option */}
                <div style={{ marginTop: 'var(--spacing-4)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)' }}>
                    <input
                      type="checkbox"
                      id="useWebSearch"
                      checked={useWebSearch}
                      onChange={(e) => setUseWebSearch(e.target.checked)}
                      style={{ transform: 'scale(1.2)' }}
                    />
                    <label htmlFor="useWebSearch" style={{ 
                      fontWeight: 600,
                      color: 'var(--color-label)',
                      cursor: 'pointer'
                    }}>
                      🔍 Use Web Search for Market Insights
                    </label>
                  </div>
                </div>

                {/* Content Type Configuration */}
                <div style={{ marginTop: 'var(--spacing-4)' }}>
                  <h4 style={{ 
                    color: 'var(--color-label)', 
                    marginBottom: 'var(--spacing-3)',
                    fontSize: 'var(--font-size-lg)',
                    fontWeight: 600
                  }}>
                    Content to Generate
                  </h4>
                
                <div style={{ display: 'grid', gap: 'var(--spacing-3)' }}>
                  {/* Images */}
                  <div style={{
                    padding: 'var(--spacing-3)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    background: 'var(--color-systemFill)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)' }}>
                      <input
                        type="checkbox"
                        id="generateImages"
                        checked={contentConfig.generateImages}
                        onChange={(e) => setContentConfig(prev => ({
                          ...prev,
                          generateImages: e.target.checked
                        }))}
                        style={{ cursor: 'pointer' }}
                      />
                      <label htmlFor="generateImages" style={{ 
                        color: 'var(--color-label)', 
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-1)'
                      }}>
                        🎨 Image Prompts
                      </label>
                    </div>
                    {contentConfig.generateImages && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                        <label style={{ color: 'var(--color-secondaryLabel)', fontSize: 'var(--font-size-sm)' }}>
                          Quantity:
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="10"
                          value={contentConfig.imageQuantity}
                          onChange={(e) => setContentConfig(prev => ({
                            ...prev,
                            imageQuantity: Math.max(1, Math.min(10, parseInt(e.target.value) || 1))
                          }))}
                          style={{
                            width: '60px',
                            padding: 'var(--spacing-1)',
                            border: '1px solid var(--color-border)',
                            borderRadius: '4px',
                            background: 'var(--color-systemBackground)',
                            color: 'var(--color-label)',
                            textAlign: 'center'
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Titles */}
                  <div style={{
                    padding: 'var(--spacing-3)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    background: 'var(--color-systemFill)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)' }}>
                      <input
                        type="checkbox"
                        id="generateTitles"
                        checked={contentConfig.generateTitles}
                        onChange={(e) => setContentConfig(prev => ({
                          ...prev,
                          generateTitles: e.target.checked
                        }))}
                        style={{ cursor: 'pointer' }}
                      />
                      <label htmlFor="generateTitles" style={{ 
                        color: 'var(--color-label)', 
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-1)'
                      }}>
                        📝 Titles
                      </label>
                    </div>
                    {contentConfig.generateTitles && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                        <label style={{ color: 'var(--color-secondaryLabel)', fontSize: 'var(--font-size-sm)' }}>
                          Quantity:
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="15"
                          value={contentConfig.titleQuantity}
                          onChange={(e) => setContentConfig(prev => ({
                            ...prev,
                            titleQuantity: Math.max(1, Math.min(15, parseInt(e.target.value) || 1))
                          }))}
                          style={{
                            width: '60px',
                            padding: 'var(--spacing-1)',
                            border: '1px solid var(--color-border)',
                            borderRadius: '4px',
                            background: 'var(--color-systemBackground)',
                            color: 'var(--color-label)',
                            textAlign: 'center'
                          }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Headlines */}
                  <div style={{
                    padding: 'var(--spacing-3)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    background: 'var(--color-systemFill)',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-2)' }}>
                      <input
                        type="checkbox"
                        id="generateHeadlines"
                        checked={contentConfig.generateHeadlines}
                        onChange={(e) => setContentConfig(prev => ({
                          ...prev,
                          generateHeadlines: e.target.checked
                        }))}
                        style={{ cursor: 'pointer' }}
                      />
                      <label htmlFor="generateHeadlines" style={{ 
                        color: 'var(--color-label)', 
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-1)'
                      }}>
                        🎯 Headlines
                      </label>
                    </div>
                    {contentConfig.generateHeadlines && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                        <label style={{ color: 'var(--color-secondaryLabel)', fontSize: 'var(--font-size-sm)' }}>
                          Quantity:
                        </label>
                        <input
                          type="number"
                          min="1"
                          max="15"
                          value={contentConfig.headlineQuantity}
                          onChange={(e) => setContentConfig(prev => ({
                            ...prev,
                            headlineQuantity: Math.max(1, Math.min(15, parseInt(e.target.value) || 1))
                          }))}
                          style={{
                            width: '60px',
                            padding: 'var(--spacing-1)',
                            border: '1px solid var(--color-border)',
                            borderRadius: '4px',
                            background: 'var(--color-systemBackground)',
                            color: 'var(--color-label)',
                            textAlign: 'center'
                          }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div style={{ marginTop: 'var(--spacing-6)', textAlign: 'center' }}>
                {inputMethod === 'quick' && (
                  <button
                    onClick={handleQuickGenerate}
                    disabled={loading || !productInfo.trim() || (!contentConfig.generateImages && !contentConfig.generateTitles && !contentConfig.generateHeadlines)}
                    style={{
                      padding: 'var(--spacing-3) var(--spacing-6)',
                      border: 'none',
                      borderRadius: '12px',
                      background: loading || !productInfo.trim() || (!contentConfig.generateImages && !contentConfig.generateTitles && !contentConfig.generateHeadlines) 
                        ? 'var(--color-systemGray)' 
                        : 'var(--gradient-primarySuccess)',
                      color: 'white',
                      cursor: loading || !productInfo.trim() || (!contentConfig.generateImages && !contentConfig.generateTitles && !contentConfig.generateHeadlines)
                        ? 'not-allowed' 
                        : 'pointer',
                      fontWeight: 700,
                      fontSize: 'var(--font-size-lg)',
                      boxShadow: 'var(--shadow-primaryGlow)',
                      transition: 'all 0.2s',
                      transform: loading ? 'scale(1)' : 'scale(1.02)',
                    }}
                  >
                    {loading ? (
                      <>
                        🔄 Generating with {campaignInstructions[selectedCampaignType]?.name || 'AI'}...
                      </>
                    ) : (
                      <>
                        ✨ Generate {campaignInstructions[selectedCampaignType]?.name || 'Creative'} Content
                        {useWebSearch && ' with Web Search'}
                      </>
                    )}
                  </button>
                )}

                {inputMethod === 'text' && (
                  <button
                    onClick={handleManualTextSubmit}
                    disabled={loading || !manualText.trim()}
                    style={{
                      padding: 'var(--spacing-3) var(--spacing-6)',
                      border: 'none',
                      borderRadius: '12px',
                      background: loading || !manualText.trim() ? 'var(--color-systemGray)' : 'var(--color-primary)',
                      color: 'white',
                      cursor: loading || !manualText.trim() ? 'not-allowed' : 'pointer',
                      fontWeight: 700,
                      fontSize: 'var(--font-size-lg)',
                    }}
                  >
                    {loading ? '🔄 Analyzing...' : '📊 Analyze Text Content'}
                  </button>
                )}

                {inputMethod === 'ai_search' && searchResults.length === 0 && (
                  <p style={{ 
                    color: 'var(--color-secondaryLabel)', 
                    fontStyle: 'italic',
                    margin: 0
                  }}>
                    Search for content above to get started with AI-powered content generation
                  </p>
                )}

                {(!contentConfig.generateImages && !contentConfig.generateTitles && !contentConfig.generateHeadlines) && (
                  <p style={{ 
                    color: 'var(--color-systemRed)', 
                    fontSize: 'var(--font-size-sm)', 
                    marginTop: 'var(--spacing-2)',
                    fontWeight: 500
                  }}>
                    Please select at least one content type to generate
                  </p>
                )}
              </div>

              {/* Hidden File Input */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.csv,.json,.docx,.txt"
                onChange={handleFileUpload}
                style={{ display: 'none' }}
              />

              {/* Loading indicator for file upload */}
              {loading && inputMethod === 'file' && (
                <div style={{ 
                  marginTop: 'var(--spacing-4)', 
                  textAlign: 'center',
                  color: 'var(--color-systemBlue)'
                }}>
                  <div style={{ fontSize: '20px', marginBottom: 'var(--spacing-2)' }}>🔄</div>
                  Analyzing your data...
                </div>
              )}
            </div>
          )}

          {/* Step 2: Review Analysis */}
          {currentStep === 'analyze' && dataAnalysis && (
            <div>
              <h3 style={{ color: 'var(--color-label)', marginBottom: 'var(--spacing-3)' }}>
                📈 Data Analysis Complete
              </h3>
              
              <div style={{
                background: 'var(--color-systemFill)',
                padding: 'var(--spacing-3)',
                borderRadius: '8px',
                marginBottom: 'var(--spacing-4)',
              }}>
                <h4 style={{ color: 'var(--color-label)', marginBottom: 'var(--spacing-2)' }}>
                  File: {dataAnalysis.filename}
                </h4>
                <p style={{ color: 'var(--color-secondaryLabel)', marginBottom: 'var(--spacing-2)' }}>
                  <strong>Type:</strong> {dataAnalysis.data_type.toUpperCase()} • 
                  <strong> Size:</strong> {(dataAnalysis.analysis_metadata.file_size / 1024).toFixed(1)} KB • 
                  <strong> Content:</strong> {dataAnalysis.analysis_metadata.content_length} characters
                </p>
                <div style={{
                  background: 'var(--color-systemBackground)',
                  padding: 'var(--spacing-2)',
                  borderRadius: '6px',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--color-secondaryLabel)',
                  maxHeight: '120px',
                  overflow: 'auto',
                }}>
                  {dataAnalysis.extracted_content}
                </div>
              </div>

              <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                <button
                  onClick={() => setCurrentStep('upload')}
                  style={{
                    padding: 'var(--spacing-2) var(--spacing-4)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    background: 'var(--color-systemBackground)',
                    color: 'var(--color-label)',
                    cursor: 'pointer',
                  }}
                >
                  Back
                </button>
                <button
                  onClick={generateContent}
                  disabled={loading}
                  style={{
                    padding: 'var(--spacing-2) var(--spacing-4)',
                    border: 'none',
                    borderRadius: '8px',
                    background: loading ? 'var(--color-systemGray)' : 'var(--color-systemBlue)',
                    color: 'white',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontWeight: 600,
                  }}
                >
                  {loading ? '🔄 Generating...' : '✨ Generate Creative Content'}
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Results */}
          {currentStep === 'results' && generatedContent && (
            <div>
              <h3 style={{ color: 'var(--color-label)', marginBottom: 'var(--spacing-3)' }}>
                ✨ Generated Creative Content
              </h3>

              {/* Image Prompts */}
              {generatedContent.images && (
                <div style={{ marginBottom: 'var(--spacing-4)' }}>
                  <h4 style={{ color: 'var(--color-label)', marginBottom: 'var(--spacing-2)' }}>
                    🎨 Image Prompts
                  </h4>
                  <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
                    {generatedContent.images.map((image, index) => (
                      <div
                        key={index}
                        onClick={() => toggleImagePrompt(image.prompt)}
                        style={{
                          padding: 'var(--spacing-3)',
                          border: `2px solid ${selectedImagePrompts.includes(image.prompt) ? 'var(--color-systemBlue)' : 'var(--color-border)'}`,
                          borderRadius: '8px',
                          cursor: 'pointer',
                          background: selectedImagePrompts.includes(image.prompt) ? 'var(--color-primaryTranslucent)' : 'var(--color-systemBackground)',
                          transition: 'all 0.2s',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-1)' }}>
                          <span style={{ fontSize: '16px' }}>
                            {selectedImagePrompts.includes(image.prompt) ? '✅' : '⭕'}
                          </span>
                          <strong style={{ color: 'var(--color-label)' }}>{image.style}</strong>
                          <span style={{ 
                            background: 'var(--color-systemFill)',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-secondaryLabel)',
                          }}>
                            {image.concept}
                          </span>
                        </div>
                        <p style={{ color: 'var(--color-secondaryLabel)', fontSize: 'var(--font-size-sm)', margin: 0 }}>
                          {image.prompt}
                        </p>
                      </div>
                    ))}
                  </div>
                  
                  {selectedImagePrompts.length > 0 && clientId && (
                    <button
                      onClick={generateImages}
                      disabled={loading}
                      style={{
                        marginTop: 'var(--spacing-3)',
                        padding: 'var(--spacing-2) var(--spacing-4)',
                        border: 'none',
                        borderRadius: '8px',
                        background: loading ? 'var(--color-systemGray)' : 'var(--color-systemGreen)',
                        color: 'white',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        fontWeight: 600,
                      }}
                    >
                      {loading ? '🔄 Generating Images...' : `🎨 Generate ${selectedImagePrompts.length} AI Images`}
                    </button>
                  )}
                </div>
              )}

              {/* Titles */}
              {generatedContent.titles && (
                <div style={{ marginBottom: 'var(--spacing-4)' }}>
                  <h4 style={{ color: 'var(--color-label)', marginBottom: 'var(--spacing-2)' }}>
                    📝 Generated Titles
                  </h4>
                  <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
                    {generatedContent.titles.map((title, index) => (
                      <div
                        key={index}
                        style={{
                          padding: 'var(--spacing-3)',
                          border: '1px solid var(--color-border)',
                          borderRadius: '8px',
                          background: 'var(--color-systemBackground)',
                        }}
                      >
                        <p style={{ color: 'var(--color-label)', fontWeight: 600, marginBottom: 'var(--spacing-1)' }}>
                          {title.text}
                        </p>
                        <div style={{ display: 'flex', gap: 'var(--spacing-2)', fontSize: 'var(--font-size-xs)' }}>
                          <span style={{ 
                            background: 'var(--color-systemFill)',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            color: 'var(--color-secondaryLabel)',
                          }}>
                            {title.approach}
                          </span>
                          <span style={{ 
                            background: 'var(--color-systemFill)',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            color: 'var(--color-secondaryLabel)',
                          }}>
                            {title.target_emotion}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Headlines */}
              {generatedContent.headlines && (
                <div style={{ marginBottom: 'var(--spacing-4)' }}>
                  <h4 style={{ color: 'var(--color-label)', marginBottom: 'var(--spacing-2)' }}>
                    🎯 Generated Headlines
                  </h4>
                  <div style={{ display: 'grid', gap: 'var(--spacing-2)' }}>
                    {generatedContent.headlines.map((headline, index) => (
                      <div
                        key={index}
                        style={{
                          padding: 'var(--spacing-3)',
                          border: '1px solid var(--color-border)',
                          borderRadius: '8px',
                          background: 'var(--color-systemBackground)',
                        }}
                      >
                        <p style={{ color: 'var(--color-label)', fontWeight: 600, marginBottom: 'var(--spacing-1)' }}>
                          {headline.text}
                        </p>
                        <div style={{ display: 'flex', gap: 'var(--spacing-2)', fontSize: 'var(--font-size-xs)' }}>
                          <span style={{ 
                            background: 'var(--color-systemFill)',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            color: 'var(--color-secondaryLabel)',
                          }}>
                            {headline.length}
                          </span>
                          <span style={{ 
                            background: 'var(--color-systemFill)',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            color: 'var(--color-secondaryLabel)',
                          }}>
                            {headline.style}
                          </span>
                          <span style={{ 
                            background: 'var(--color-systemFill)',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            color: 'var(--color-secondaryLabel)',
                          }}>
                            {headline.cta_focus}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
                <button
                  onClick={() => setCurrentStep('analyze')}
                  style={{
                    padding: 'var(--spacing-2) var(--spacing-4)',
                    border: '1px solid var(--color-border)',
                    borderRadius: '8px',
                    background: 'var(--color-systemBackground)',
                    color: 'var(--color-label)',
                    cursor: 'pointer',
                  }}
                >
                  Back
                </button>
                <button
                  onClick={() => { setIsOpen(false); resetState() }}
                  style={{
                    padding: 'var(--spacing-2) var(--spacing-4)',
                    border: 'none',
                    borderRadius: '8px',
                    background: 'var(--color-systemBlue)',
                    color: 'white',
                    cursor: 'pointer',
                    fontWeight: 600,
                  }}
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
} 